import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import {
  AdminPartnerPrice,
  Partner,
  PartnerUser,
} from '@returnmates/client-core/src/graphql/generated/api'
import { getPartners } from '@returnmates/client-core/src/selectors/admin'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import compareArrayOfObjects from '@returnmates/client-core/src/utils/compareArrayOfObjects'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import validatePartner from '@returnmates/client-core/src/utils/validation/partner'
import Button from '@returnmates/ui-core/src/components/Button'
import ErrorBlock from '@returnmates/ui-core/src/components/ErrorBlock'
import clsx from 'clsx'
import moment from 'moment-timezone'
import { memo, useCallback, useContext, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'

import { FlyoutSidebarContext } from '../../pages/Main'
import EditPartnerFormInner from './components/PartnerFormInner'
import { initialFormValues } from './constants'
import useStyles from './styles'

interface Props {
  partnerCode: string
}

const PartnerForm = ({ partnerCode }: Props) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const partners = useSelector(getPartners)
  const { handleCloseSidebar } = useContext(FlyoutSidebarContext)
  const [isUpdatePartnerLoading, setIsUpdatePartnerLoading] = useState(false)

  const currentPartner = useMemo(() => partners.find(p => p.code === partnerCode), [
    partners,
    partnerCode,
  ])

  const initialValues = useMemo(
    () =>
      currentPartner ? { ...currentPartner, bccEmails: currentPartner.bccEmails?.join(',') } : null,
    [currentPartner],
  )

  const handleUpdatePartner = useCallback(
    async formPartner => {
      setIsUpdatePartnerLoading(true)
      try {
        const newPartner = { ...formPartner }
        delete newPartner.prices
        delete newPartner.retailer
        delete newPartner.__typename
        delete newPartner.zipCodes
        delete newPartner.destinations
        delete newPartner.rescheduleLimit

        const bccEmailsArr = `{${newPartner.bccEmails
          ?.split(',')
          .map((email: string) => email.trim())
          .join(',') || ''}}`

        await createAsyncAction(
          dispatch,
          actions.updatePartner.request({
            code: newPartner.code,
            input: {
              ...initialFormValues,
              ...newPartner,
              code: undefined,
              schedulerLimit: Number(newPartner.schedulerLimit),
              orderExpire: Number(newPartner.orderExpire),
              offerExpiresOn: newPartner.offerExpiresOn
                ? moment(newPartner.offerExpiresOn).format('YYYY-MM-DD')
                : undefined,
              attributes: undefined,
              partnerLabelTypes: undefined,
              priceHubs: undefined,
              users: undefined,
              desktopLogo: newPartner.desktopLogo || undefined,
              mobileLogo: newPartner.mobileLogo || undefined,
              headerImageUrl: newPartner.headerImageUrl || undefined,
              tpLogo: newPartner.tpLogo || undefined,
              bccEmails: bccEmailsArr,
            },
          }),
        )

        const {
          create: createAttributes,
          update: updateAttributes,
          delete: deleteAttributes,
        } = compareArrayOfObjects(
          (currentPartner as Partner).attributes as {
            [x: string]: string | number | boolean | null
          }[],
          formPartner.attributes,
          'id',
        )

        for (const attribute of createAttributes) {
          const createdAttr = await createAsyncAction(
            dispatch,
            actions.createPartnerAttribute.request({
              input: {
                partnerCode: formPartner.code,
                name: attribute.name,
                title: attribute.title,
                placeholder: attribute.placeholder || '',
                type: attribute.type,
                isRequired: attribute.isRequired || false,
                tooltip: attribute.tooltip || '',
              },
            }),
          )

          dispatch(
            actions.createPartnerAttributeLocally.request({
              attribute: createdAttr,
              code: formPartner.code,
            }),
          )
        }

        for (const attribute of updateAttributes) {
          await createAsyncAction(
            dispatch,
            actions.updatePartnerAttribute.request({
              input: {
                id: attribute.id,
                partnerCode: formPartner.code,
                name: attribute.name,
                title: attribute.title,
                placeholder: attribute.placeholder || '',
                type: attribute.type,
                isRequired: attribute.isRequired || false,
                tooltip: attribute.tooltip || '',
              },
            }),
          )
        }

        for (const attribute of deleteAttributes) {
          await createAsyncAction(
            dispatch,
            actions.deletePartnerAttribute.request({
              id: attribute.id,
            }),
          )
        }

        const {
          create: createPrice,
          update: updatePrice,
          delete: deletePrice,
        } = compareArrayOfObjects(
          (currentPartner as Partner).priceHubs as AdminPartnerPrice[],
          formPartner.priceHubs,
          'hubId',
        )

        for (const price of createPrice) {
          await createAsyncAction(
            dispatch,
            actions.createPartnerPrice.request({
              input: {
                partnerCode: formPartner.code,
                hubId: price.hubId,
                price: price.price,
              },
            }),
          )
        }

        for (const price of updatePrice) {
          await createAsyncAction(
            dispatch,
            actions.updatePartnerPrice.request({
              input: {
                partnerCode: formPartner.code,
                hubId: price.hubId,
                price: price.price,
              },
            }),
          )
        }

        for (const price of deletePrice) {
          await createAsyncAction(
            dispatch,
            actions.deletePartnerPrice.request({
              partnerCode: formPartner.code,
              hubId: price.hubId,
            }),
          )
        }

        const { create: createUser, delete: deleteUser } = compareArrayOfObjects(
          (currentPartner as Partner).users as PartnerUser[],
          formPartner.users,
          'hubId',
        )

        for (const user of createUser) {
          await createAsyncAction(
            dispatch,
            actions.createUserForPartner.request({
              input: {
                userId: user.userId,
                name: user.name,
                email: user.email,
              },
            }),
          )
        }

        for (const user of deleteUser) {
          await createAsyncAction(
            dispatch,
            actions.deleteUserFromPartner.request({
              userId: user.userId,
            }),
          )
        }
      } catch (err) {
        const { message } = err as Error

        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: errorMapper(message || (err as string)),
          }),
        )
      } finally {
        setIsUpdatePartnerLoading(false)
      }
    },
    [currentPartner, dispatch],
  )

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleUpdatePartner}
      validate={validatePartner}
      render={({ handleSubmit, submitError }) => (
        <>
          <ErrorBlock message={submitError} className={classes.errorBlock} />
          <EditPartnerFormInner partnerCode={partnerCode} />
          <div className={classes.buttonsContainer}>
            <Button
              onClick={handleCloseSidebar}
              label="Cancel"
              className={clsx(classes.button, classes.cancelButton)}
              cancelButton
            />
            <Button
              onClick={handleSubmit}
              label="Save"
              isLoading={isUpdatePartnerLoading}
              className={clsx(classes.button, classes.saveButton)}
            />
          </div>
        </>
      )}
    />
  )
}

export default memo(PartnerForm)
